import { fetchBaaStatus } from "@app/agreements";
import { formatLatencyMs } from "@app/date";
import {
  fetchLlmGatewayConfiguration,
  fetchLlmGatewayKeys,
  formatCurrency,
  selectLlmGatewayConfiguration,
} from "@app/deploy";
import {
  type LlmGatewayKeyRow,
  currentMonthWindow,
  fetchLlmGatewayKeysUsage,
  selectActiveLlmGatewayKeyIds,
  selectLlmGatewayKeysWithUsage,
} from "@app/llm-gateway";
import { compactNumber } from "@app/number-utils";
import {
  selectOrganizationSelected,
  selectOrganizationSelectedId,
} from "@app/organizations";
import {
  useCompositeLoader,
  useLoader,
  useQuery,
  useSelector,
} from "@app/react";
import {
  aiGatewayEnrollUrl,
  llmGatewayKeyDetailUrl,
  settingsLegalAgreementsUrl,
  supportUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import { maskedTokenPrefix } from "@app/string-utils";
import { tunaEvent } from "@app/tuna";
import type {
  AgreementSummary,
  DeployLlmGatewayKey,
  LlmGatewayUsage,
} from "@app/types";
import { Banner } from "@app/ui/shared/banner";
import { Button, ButtonLink } from "@app/ui/shared/button";
import { CollapsibleBanner } from "@app/ui/shared/collapsible-banner";
import { DataTable, idColDef } from "@app/ui/shared/datatable";
import {
  CurrencyCell,
  DataTableCell,
  DateTimeCell,
  PillCell,
} from "@app/ui/shared/datatable/cells";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
} from "@app/ui/shared/datatable/datatable";
import { ExternalLink } from "@app/ui/shared/external-link";
import { Group } from "@app/ui/shared/group";
import { IconInfo, IconKey } from "@app/ui/shared/icons";
import { LoadingSpinner } from "@app/ui/shared/loading";
import {
  ProgressBar,
  type ProgressBarVariant,
  progressBarTextColor,
} from "@app/ui/shared/progress-bar";
import { TitleBar } from "@app/ui/shared/resource-list-view";
import { tokens } from "@app/ui/shared/tokens";
import { Tooltip } from "@app/ui/shared/tooltip";
import { Fragment, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AppSidebarLayout } from "../layouts";
import {
  CreateLlmGatewayKeyModal,
  LlmGatewayKeyTokenModal,
} from "../shared/llm-gateway-key/create-llm-gateway-key-modal";
import { LegacyLlmKeySection } from "../shared/llm-gateway-key/legacy-llm-key-table";
import { RevokeLlmGatewayKeyModal } from "../shared/llm-gateway-key/revoke-llm-gateway-key-modal";
import { LlmGatewayUsageSummary } from "../shared/llm-gateway-usage/llm-gateway-usage-summary";

const TITLE_DESCRIPTION =
  "LLM Gateway keys provide secure access to the Aptible LLM Gateway for your applications.";

const UsageCell = ({
  row,
  children,
}: {
  row: LlmGatewayKeyRow;
  children: (usage: LlmGatewayUsage) => React.ReactNode;
}) => {
  const { isLoading } = useLoader(fetchLlmGatewayKeysUsage);

  if (row.usage) {
    return <>{children(row.usage)}</>;
  }
  if (isLoading && row.status === "ACTIVE") {
    return <LoadingSpinner variant="xs" />;
  }
  return <span className="text-gray-500">—</span>;
};

const UsageBreakdownCell = ({
  total,
  breakdown,
}: {
  total: number;
  breakdown: [string, number][];
}) => (
  <Tooltip
    fluid
    text={
      <div className="grid grid-cols-2 gap-1">
        {breakdown.map(([label, value]) => (
          <Fragment key={label}>
            <div className="font-semibold">{label}</div>
            <div className="text-right">{value.toLocaleString()}</div>
          </Fragment>
        ))}
      </div>
    }
  >
    <span className="border-b border-dotted border-gray-400">
      {compactNumber(total)}
    </span>
  </Tooltip>
);

const spendLimitVariant = (fraction: number): ProgressBarVariant => {
  if (fraction >= 0.9) return "error";
  if (fraction >= 0.75) return "warning";
  return "default";
};

const SpendCell = ({
  spend,
  limit,
}: { spend: number; limit: number | null }) => {
  if (limit === null) {
    return <CurrencyCell value={spend} />;
  }

  // A key with a limit of $0 can't spend anything, so it reads as used up.
  const fraction = limit > 0 ? spend / limit : 1;
  const variant = spendLimitVariant(fraction);

  return (
    <DataTableCell
      label={
        <>
          {formatCurrency(spend)} <strong>/ {formatCurrency(limit)}</strong>
          {variant === "default" ? null : (
            <Tooltip
              className="ml-1 inline-block align-middle"
              triggerButton
              triggerAriaLabel="Spend limit usage"
              text={`This key has used ${Math.floor(fraction * 100)}% of its spend limit. When the limit is reached, requests are blocked until the new billing cycle begins or the spend limit is increased.`}
            >
              <IconInfo
                variant="sm"
                color="currentColor"
                className={progressBarTextColor(variant)}
              />
            </Tooltip>
          )}
        </>
      }
      description={
        <ProgressBar
          className="mt-1"
          value={fraction}
          maxValue={1}
          variant={variant}
          height="md"
        />
      }
    />
  );
};

const usageColDefs: DataTableColDef<LlmGatewayKeyRow>[] = [
  {
    colId: "spend",
    headerName: "Spend (MTD)",
    valueGetter: ({ data }) => (data?.usage ? data.usage.spend : null),
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) => (
      <UsageCell row={data}>
        {(usage) => <SpendCell spend={usage.spend} limit={data.spendLimit} />}
      </UsageCell>
    ),
  },
  {
    colId: "requests",
    headerName: "Requests",
    initialHide: true,
    valueGetter: ({ data }) => (data?.usage ? data.usage.totalRequests : null),
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) => (
      <UsageCell row={data}>
        {(usage) => (
          <UsageBreakdownCell
            total={usage.totalRequests}
            breakdown={[
              ["Successful", usage.successfulRequests],
              ["Failed", usage.failedRequests],
            ]}
          />
        )}
      </UsageCell>
    ),
  },
  {
    colId: "tokens",
    headerName: "Tokens",
    initialHide: true,
    valueGetter: ({ data }) => (data?.usage ? data.usage.totalTokens : null),
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) => (
      <UsageCell row={data}>
        {(usage) => (
          <UsageBreakdownCell
            total={usage.totalTokens}
            breakdown={[
              ["Input", usage.inputTokens],
              ["Output", usage.outputTokens],
            ]}
          />
        )}
      </UsageCell>
    ),
  },
  {
    colId: "latency",
    headerName: "Avg Latency",
    initialHide: true,
    valueGetter: ({ data }) =>
      data?.usage ? data.usage.averageLatencyMs : null,
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) => (
      <UsageCell row={data}>
        {(usage) => (
          <DataTableCell label={formatLatencyMs(usage.averageLatencyMs)} />
        )}
      </UsageCell>
    ),
  },
];

const colDefs = (
  onRevoke: (key: DeployLlmGatewayKey) => void,
): DataTableColDef<LlmGatewayKeyRow>[] => [
  {
    field: "handle",
    headerName: "Key",
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) => (
      <DataTableCell
        label={data.handle}
        description={
          <span className={"font-mono tracking-wider text-xs"}>
            {maskedTokenPrefix(data.tokenPrefix)}
          </span>
        }
        href={llmGatewayKeyDetailUrl(data.id)}
        icon={
          <img
            src="/resource-types/logo-llm_key.png"
            className="w-[32px] h-[32px] mr-2 align-middle"
            aria-label="LLM Gateway Key"
          />
        }
      />
    ),
  },
  {
    field: "id",
    ...idColDef,
    valueGetter: ({ data }) => Number.parseInt(data?.id || "0", 10),
  },
  {
    field: "status",
    headerName: "Status",
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) => (
      <PillCell
        value={data.status === "ACTIVE" ? "Active" : "Revoked"}
        valueToVariantMap={{ Active: "success", Revoked: "error" }}
      />
    ),
  },
  {
    field: "createdBy",
    headerName: "Created By",
    valueGetter: ({ data }) => data?.createdBy.name,
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) => (
      <DataTableCell
        label={data.createdBy.name}
        description={data.createdBy.email}
      />
    ),
  },
  {
    field: "createdAt",
    headerName: "Created At (UTC)",
    sort: "desc",
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) => (
      <DateTimeCell date={data.createdAt} />
    ),
  },
  {
    field: "spendLimit",
    headerName: "Spend Limit",
    initialHide: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) =>
      data.spendLimit === null ? (
        <span className="text-gray-500">—</span>
      ) : (
        <CurrencyCell value={data.spendLimit} />
      ),
  },
  ...usageColDefs,
  {
    colId: "actions",
    headerName: "Actions",
    sortable: false,
    width: 100,
    resizable: false,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayKeyRow>) => (
      <Button
        variant="delete"
        size="sm"
        disabled={data.status === "REVOKED"}
        onClick={() => onRevoke(data)}
      >
        Revoke
      </Button>
    ),
  },
];

export const LlmGatewayPage = () => {
  const orgId = useSelector(selectOrganizationSelectedId);
  const org = useSelector(selectOrganizationSelected);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<{
    token: string;
    gatewayUrl: string;
  } | null>(null);
  const [keyToRevoke, setKeyToRevoke] = useState<DeployLlmGatewayKey | null>(
    null,
  );

  useQuery(fetchLlmGatewayConfiguration({ orgId }));
  useQuery(fetchLlmGatewayKeys({ orgId }));
  const loader = useCompositeLoader([
    fetchLlmGatewayConfiguration,
    fetchLlmGatewayKeys,
  ]);
  const isFetching = loader.isLoading || loader.isInitialLoading;

  const config = useSelector(selectLlmGatewayConfiguration);
  const llmGatewayKeys = useSelector((s) =>
    selectLlmGatewayKeysWithUsage(s, { orgId }),
  );
  const hasKeys = llmGatewayKeys.length > 0;
  const showWelcome = !hasKeys && !isFetching;

  const baaFetchAction = useMemo(() => fetchBaaStatus({ orgId }), [orgId]);
  useQuery(baaFetchAction);
  const baaSummary = useSelector((s) =>
    schema.cache.selectById(s, { id: baaFetchAction.payload.key }),
  ) as AgreementSummary | undefined;
  const baaStatus = baaSummary?.status;
  const missingSignedBaa = baaStatus !== undefined && baaStatus !== "SIGNED";

  const [usageWindow] = useState(currentMonthWindow);
  const activeKeyIds = useSelector((s) =>
    selectActiveLlmGatewayKeyIds(s, { orgId }),
  );
  useQuery(
    fetchLlmGatewayKeysUsage({
      orgId,
      llmKeyIds: activeKeyIds,
      ...usageWindow,
    }),
  );

  const onCreateClick = () => {
    tunaEvent(
      "new-llm-gateway-key-button-clicked",
      JSON.stringify({ orgId: org.id, orgName: org.name }),
    );
    setIsCreateModalOpen(true);
  };

  // Only the first load blocks the page. Bootup refetches the configuration
  // while this page is already up, which shouldn't tear the table down.
  if (config === null && isFetching) {
    return (
      <AppSidebarLayout>
        <Group>
          <Group size="sm">
            <TitleBar description={TITLE_DESCRIPTION}>LLM Gateway</TitleBar>
          </Group>
          <div className="flex justify-center py-12">
            <LoadingSpinner show />
          </div>
        </Group>
      </AppSidebarLayout>
    );
  }

  if (config === null) {
    return <Navigate to={aiGatewayEnrollUrl()} replace />;
  }

  return (
    <AppSidebarLayout>
      <Group>
        <Group variant="horizontal" className="items-center justify-between">
          <TitleBar description={TITLE_DESCRIPTION}>LLM Gateway</TitleBar>
          <Button onClick={onCreateClick}>
            <IconKey variant="sm" className="mr-2" /> Create Key
          </Button>
        </Group>

        <LlmGatewayUsageSummary orgId={orgId} />

        {showWelcome && (
          <CollapsibleBanner
            title="Welcome to the LLM Gateway!"
            defaultExpanded={true}
            feature="llm-gateway-welcome"
            dismissable={false}
            persistDismissed={false}
            icon={<div className="text-2xl">🎉</div>}
            extraActions={
              <ButtonLink variant="white" size="sm" to={supportUrl()}>
                Contact Support
              </ButtonLink>
            }
          >
            <div className="flex-1">
              <p className={`${tokens.type.darker} max-w-[95%]`}>
                You're on your way to using LLMs safely and securely in a
                compliant environment. Need help getting started?{" "}
                <ExternalLink
                  className="underline"
                  href="https://www.aptible.com/docs/core-concepts/ai-gateway"
                >
                  Check out the docs
                </ExternalLink>
                , or contact us if you'd like some assistance getting setup.
              </p>
            </div>
          </CollapsibleBanner>
        )}

        {missingSignedBaa && (
          <Banner variant="warning">
            <p className={`${tokens.type.darker} mt-0.5`}>
              A signed BAA with Aptible is required before you can safely send
              any PHI through the gateway.{" "}
              <Link
                to={settingsLegalAgreementsUrl()}
                target="_blank"
                className="underline"
              >
                Execute a BAA here
              </Link>
              .
            </p>
          </Banner>
        )}

        <DataTable<LlmGatewayKeyRow>
          rowData={llmGatewayKeys}
          isLoading={!hasKeys && isFetching}
          colDefs={colDefs(setKeyToRevoke)}
          syncQueryParams={true}
          defaultSearch="status:active"
        />

        <LegacyLlmKeySection />

        <CreateLlmGatewayKeyModal
          isOpen={isCreateModalOpen}
          orgSpendLimit={config.spendLimit}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={(token, gatewayUrl) =>
            setCreatedToken({ token, gatewayUrl })
          }
        />

        <LlmGatewayKeyTokenModal
          isOpen={createdToken !== null}
          token={createdToken?.token ?? ""}
          gatewayUrl={createdToken?.gatewayUrl ?? ""}
          onClose={() => setCreatedToken(null)}
        />

        <RevokeLlmGatewayKeyModal
          isOpen={keyToRevoke !== null}
          onClose={() => setKeyToRevoke(null)}
          llmGatewayKey={keyToRevoke}
        />
      </Group>
    </AppSidebarLayout>
  );
};
